import { useEffect, useState } from 'react';
import useSheetAnim from '../hooks/useSheetAnim';
import { Chip, Skeletons, RM0, RMk, dt, initials } from './kit';
import { shortAddress } from '../lib/format';
import { getPerformanceMember } from '../api/performance';

/**
 * One team member's production, opened from the performance leaderboard.
 *
 * A bottom sheet rather than a route, matching CommissionSheet — a leader scanning
 * a team checks two or three people in a row, and a navigation each way turns that
 * into six taps.
 *
 * The deal rows deliberately do NOT link into /sales/:id. That route is guarded by
 * enforceAgentOwnership, so every tap-through here would 403; loosening the guard to
 * make the link work would open the whole sales module. The sheet renders its own
 * trimmed payload instead — production facts, no counterparties.
 */
export default function MemberPerformanceSheet({ open, member, period, onClose }) {
  const { mounted, closing } = useSheetAnim(open);
  const [d, setD] = useState(null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState('closed');
  const memberId = member?.agent_id;

  useEffect(() => {
    if (!open || !memberId) return undefined;
    let live = true;
    setD(null);
    setFailed(false);
    setTab('closed');
    getPerformanceMember(memberId, {
      ...(period?.from ? { from: period.from } : {}),
      ...(period?.to ? { to: period.to } : {}),
    })
      .then((r) => { if (live) setD(r?.data ?? r); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [open, memberId, period?.from, period?.to]);

  if (!mounted || !member) return null;

  const rows = d ? (tab === 'closed' ? d.deals : d.pipeline) : [];
  const kpi = d?.kpi;

  return (
    <div
      onClick={onClose}
      className={`sheet-scrim${closing ? ' is-closing' : ''}`}
      style={{ position: 'fixed', inset: 0, zIndex: 4600, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`sheet-panel${closing ? ' is-closing' : ''}`}
        style={{ background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '88%', display: 'flex', flexDirection: 'column' }}
      >
        {/* header — drawn from the list row, so it never waits on the network */}
        <div style={{ flex: 'none', padding: '10px 16px 13px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ width: 38, height: 4, borderRadius: 999, background: 'var(--border)', margin: '0 auto 13px' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--chip)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flex: 'none' }}>
              {initials(member.name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.name}</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{memberLine(member)}</div>
            </div>
            {member.dormant
              ? <Chip label="Dormant" tone="warn" />
              : member.is_new ? <Chip label="New" tone="brand" /> : <Chip label="Active" tone="ok" />}
          </div>
        </div>

        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '13px 16px 16px', display: 'flex', flexDirection: 'column', gap: 11 }}>
          {failed ? (
            <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55 }}>
              Couldn&rsquo;t load this member&rsquo;s production just now. Pull the list to refresh and try again.
            </div>
          ) : !d ? (
            <Skeletons n={3} height={70} />
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 9 }}>
                <Tile label={`GCI · ${d.period.label}`} value={RMk(kpi.gci)} color="var(--ok)" />
                <Tile label="DEALS" value={String(kpi.deals)} />
                <Tile label="VOLUME" value={RMk(kpi.volume)} />
              </div>

              {/* Override attribution — the caller's OWN commission, so always theirs
                  to see. Absent (not zero) when this member is outside their downline:
                  a Centre Director earns nothing from someone else's recruit, and
                  "RM 0" would imply they should. */}
              {d.override && (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--primary)', borderRadius: 14, padding: '14px 15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', color: 'var(--muted)' }}>
                        OVERRIDE YOU EARNED
                      </div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--ok)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                        {RM0(d.override.amount)}
                      </div>
                    </div>
                    {d.override.gen ? <Chip label={`Gen ${d.override.gen}`} tone="brand" /> : null}
                  </div>
                  {/* Historic override lines carry no pointer to the seller's split
                      row, so a deal shared with another of your recruits is
                      apportioned by each one's share rather than read off a record.
                      Say so — an apportioned figure presented as exact is the kind
                      of number someone queries with HQ. */}
                  {d.override.estimated && (
                    <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5, marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)' }}>
                      Part of this is apportioned: some of these deals were shared with another of
                      your recruits, and the source records don&rsquo;t name which one the override came off.
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <TabChip on={tab === 'closed'} onClick={() => setTab('closed')} label={`Closed · ${kpi.deals}`} />
                <TabChip on={tab === 'pipeline'} onClick={() => setTab('pipeline')} label={`Pipeline · ${kpi.pipeline_deals}`} />
              </div>

              {rows.length === 0 ? (
                <div style={{ fontSize: 12.5, color: 'var(--muted)', padding: '18px 2px', textAlign: 'center' }}>
                  {tab === 'closed' ? `Nothing closed in ${d.period.label}.` : 'No open deals.'}
                </div>
              ) : (
                <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 14, padding: '2px 13px' }}>
                  {rows.map((r, i) => (
                    <div key={r.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, fontFamily: 'ui-monospace,monospace' }}>{r.sale_ref}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {[shortAddress(r.property), dt(r.sale_date)].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flex: 'none' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ok)', fontVariantNumeric: 'tabular-nums' }}>{RM0(r.gci)}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{r.stage_label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.55, padding: '0 2px' }}>
                Production figures only. Purchaser, vendor and tenant details are not shown,
                and this list does not open the sale record.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const memberLine = (m) => [
  m.gen ? `Gen ${m.gen}` : null,
  RANK_LABEL[m.management_rank] || m.management_rank,
  m.tier ? `${m.tier}% tier` : null,
].filter(Boolean).join(' · ');

const RANK_LABEL = { AGENT: 'Agent', TL: 'Team Leader', CD: 'Centre Director' };

const Tile = ({ label, value, color }) => (
  <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 13, padding: '10px 11px', minWidth: 0 }}>
    <div style={{ fontSize: 10.5, color: 'var(--muted)', fontWeight: 700, letterSpacing: '.05em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 3, color: color || 'var(--text)', whiteSpace: 'nowrap' }}>{value}</div>
  </div>
);

const TabChip = ({ on, onClick, label }) => (
  <span
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    style={{
      fontSize: 11.5, fontWeight: 600, padding: '6px 13px', borderRadius: 999, cursor: 'pointer',
      border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
      background: on ? 'var(--primary)' : 'var(--surface)',
      color: on ? '#fff' : 'var(--muted)',
    }}
  >
    {label}
  </span>
);
